/**
 * F32 (Wave 2C) — admin open-game reports queue page.
 *
 * Route: `/open-game-reports`
 * Permission: `open_games:read` gates the page render; the Resolve action is
 * additionally gated on `open_games:moderate` (super_admin + content_moderator).
 *
 * Chip-row status filter (Open / Reviewed / Dismissed / All via `?status=`);
 * default = Open (the actionable queue, which drives the sidebar pending-count
 * badge). Each row exposes a Resolve CTA (reviewed/dismissed) + a link to the
 * reported game's detail page. Mirrors the F31 skill-disputes queue shape.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Chip,
  EmptyState,
  Pagination,
  Skeleton,
  Stat,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import {
  OPEN_GAME_REPORT_STATUSES,
  type OpenGameReport,
  type OpenGameReportResolution,
  type OpenGameReportStatus,
} from '@bravemove/types';
import { adminOpenGamesApi, type ListReportsOpts } from '../../api/openGames';
import { PageHeader } from '../../components/PageHeader';
import { ResolveOpenGameReportDialog } from '../../components/open-games/ResolveOpenGameReportDialog';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { usePageKpis } from '../../hooks/usePageKpis';

const PAGE_SIZE = 25;

type UiFilter = OpenGameReportStatus | 'all';
const DEFAULT_FILTER: UiFilter = 'open';
const FILTER_TABS: ReadonlyArray<UiFilter> = [...OPEN_GAME_REPORT_STATUSES, 'all'];

const STATUS_CHIP_MAP: Record<OpenGameReportStatus, StatusChipStatus> = {
  open: 'in_review',
  reviewed: 'approved',
  dismissed: 'pending',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminOpenGameReportsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterParam = (searchParams.get('status') as UiFilter | null) ?? DEFAULT_FILTER;
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [rows, setRows] = useState<readonly OpenGameReport[]>([]);
  const [total, setTotal] = useState(0);
  // F61 (A3) — one unaudited `GET /admin/page-kpis` (was 2–4 audited
  // `perPage=1` list reads); refreshed after any admin action.
  const kpiCounts = usePageKpis('open_game_reports');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const canModerate = auth.adminUser?.permissions.includes('open_games:moderate') ?? false;

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: ListReportsOpts = { status: filterParam, page: pageParam, perPage: PAGE_SIZE };
      const data = await adminOpenGamesApi.getReports(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [filterParam, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onFilterChange = (value: UiFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_FILTER) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const handleResolve = async (resolution: OpenGameReportResolution): Promise<void> => {
    if (!resolveId) return;
    setResolveError(null);
    try {
      await adminOpenGamesApi.resolveReport(resolveId, { resolution });
      setResolveId(null);
      toast.push({
        tone: 'success',
        title: t('open-game:admin.reports_queue.resolve', { defaultValue: 'Resolved' }),
      });
      await fetchList();
    } catch (err) {
      setResolveError(translateApiError(err, t));
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<OpenGameReport>[]>(
    () => [
      {
        key: 'reason',
        header: t('open-game:report_dialog.reason_label', { defaultValue: 'Reason' }),
        cell: (row) => (
          <span className="text-[13px] text-ink">{t(`open-game:report_reason.${row.reason}`)}</span>
        ),
      },
      {
        key: 'details',
        header: t('open-game:report_dialog.details_label', { defaultValue: 'Details' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 line-clamp-1 max-w-[28ch]">
            {row.details ?? '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('open-game:admin.list.filter_status', { defaultValue: 'Status' }),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={STATUS_CHIP_MAP[row.status]}
            label={t(`open-game:admin.report_status.${row.status}`)}
          />
        ),
      },
      {
        key: 'created_at',
        header: t('open-game:admin.detail.reports', { defaultValue: 'Reported' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.createdAt, locale)}</span>
        ),
      },
      {
        key: 'actions',
        header: '',
        cell: (row) => (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="text-[12px] text-accent hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/open-games/${row.openGameId}`);
              }}
              data-testid={`view-game-${row.id}`}
            >
              {t('open-game:admin.detail.title', { defaultValue: 'Open game' })}
            </button>
            {canModerate && row.status === 'open' ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setResolveError(null);
                  setResolveId(row.id);
                }}
                data-testid={`resolve-${row.id}`}
              >
                {t('open-game:admin.reports_queue.resolve', { defaultValue: 'Resolve' })}
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [t, locale, navigate, canModerate],
  );

  return (
    <div className="space-y-6" data-testid="open-game-reports-page">
      <PageHeader
        title={t('open-game:admin.reports_queue.title', { defaultValue: 'Open game reports' })}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3" data-testid="open-game-reports-kpi-strip">
        <Stat
          label={t('open-game:admin.reports_kpi.open', { defaultValue: 'Open reports' })}
          value={kpiCounts === null ? '—' : kpiCounts.open}
        />
        <Stat
          label={t('open-game:admin.reports_kpi.reviewed', { defaultValue: 'Reviewed reports' })}
          value={kpiCounts === null ? '—' : kpiCounts.reviewed}
        />
        <Stat
          label={t('open-game:admin.reports_kpi.dismissed', { defaultValue: 'Dismissed reports' })}
          value={kpiCounts === null ? '—' : kpiCounts.dismissed}
        />
      </div>

      <div
        role="tablist"
        aria-label={t('open-game:admin.list.filter_status', { defaultValue: 'Status' })}
        className="flex flex-wrap items-center gap-2"
      >
        {FILTER_TABS.map((tab) => (
          <Chip
            key={tab}
            selected={filterParam === tab}
            onClick={() => onFilterChange(tab)}
            data-testid={`open-game-reports-tab-${tab}`}
          >
            {tab === 'all'
              ? t('admin:nav.all', { defaultValue: 'All' })
              : t(`open-game:admin.report_status.${tab}`)}
          </Chip>
        ))}
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="open-game-reports-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('open-game:admin.reports_queue.empty', {
            defaultValue: 'No reports to review.',
          })}
        />
      ) : (
        <>
          <Table
            data={rows as OpenGameReport[]}
            columns={columns as TableColumn<OpenGameReport>[]}
            rowKey={(row) => row.id}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination page={pageParam} pageCount={pageCount} onChange={onPageChange} />
            </div>
          ) : null}
        </>
      )}

      <ResolveOpenGameReportDialog
        open={resolveId !== null}
        onResolve={handleResolve}
        onClose={() => setResolveId(null)}
        errorMessage={resolveError}
      />
    </div>
  );
}

export default AdminOpenGameReportsPage;
